import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RemoveIcon from '@mui/icons-material/Remove';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

import { updateBranchTime } from '../../../api/center-branch';
import { updateBranchTrainerOrder } from '../../../api/staff';
import { updateBranchScheduleSetting } from '../../../api/branchScheduleSetting';
import { errorMessages } from '../../../utils/methods';
import { filterRoomsByLessonType, getRoomId } from '../../../constants/roomTypes';
import ClassroomManageModal from './ClassroomManageModal';
import {
  CARD_MIN_OPTIONS,
  DEFAULT_CARD_MIN_MINUTES,
  COUNT_CHIPS,
  DURATION_MAX,
  DURATION_MIN,
  DURATION_STEP,
  DURATION_TYPES,
  ROOM_DEFAULT_TYPES,
  TYPE_COUNT_KEY_TO_CLASS_TYPE,
  TIME_UNIT_OPTIONS,
  SNAP_OPTIONS,
  branchScheduleSettingQueryKey,
  isValidDuration,
  toMultilineToast,
} from './branchScheduleSetting';
import { ATTEND_STATUS_META, SUMMARY_EXTRA_COUNT_META } from './PrivateLessonStyled';
import { WEEK_START_DAY_LABEL, WEEK_START_DAY_OPTIONS } from '../weekStart';

// 기존 CRM 모달 톤(SchedulePasteDialogs) — 헤더 다크, 주 버튼 청록. 그룹 스케줄 설정 모달도 같은 조각을 가져다 쓴다
export const HEADER_BG = '#262626';
export const PRIMARY = '#4ab3bc';
export const PRIMARY_DARK = '#2E8A90';
const LABEL_WIDTH = '96px';

// 캘린더 시작·종료 시각 선택지 (기존 시간 설정 모달과 동일: 00:00 ~ 24:00)
export const HOURS = Array.from({ length: 25 }, (_, index) => ({
  value: index,
  title: `${String(index).padStart(2, '0')}:00`,
}));

export const TIME_UNIT_LABEL = { 30: '30분', 60: '1시간', 120: '2시간' };
export const toSnapLabel = minutes => (minutes === 60 ? '1시간' : `${minutes}분`);
// 표 칸 단위를 바꿀 때 — 선택 단위가 새 표 칸보다 크면 표 칸 값으로 낮춘다(선택 구간이 칸보다 크면 윗 칸으로 넘어가 시각이 당겨진다)
export const withTimeUnit = value => prev => ({
  ...prev,
  timeUnitMinutes: value,
  snapMinutes: Math.min(prev.snapMinutes, value),
});

// 카드 최소 높이는 분으로 저장하고 화면에는 1시간 칸 대비 % 로 보여준다 (15분 → 25%, 48분 → 80%)
export const toCardMinPercent = minutes => Math.round((minutes / 60) * 100);

// 표시 항목 탭 — 유형 항목(카드 포함)은 끄면 일정 자체를 숨기고, 나머지는 숫자만 숨긴다.
// 점 색은 지점 스케줄 상단 요약 칩 숫자 색과 같다.
const VISIBLE_ITEM_COLOR = {
  SCHEDULE: SUMMARY_EXTRA_COUNT_META.schedule.color,
  RESERVED: ATTEND_STATUS_META.reserved.countColor,
  ATTENDED: ATTEND_STATUS_META.attended.countColor,
  ABSENT: ATTEND_STATUS_META.absented.countColor,
  GROUP: SUMMARY_EXTRA_COUNT_META.group.color,
  OT: SUMMARY_EXTRA_COUNT_META.ot.color,
  CONSULT: SUMMARY_EXTRA_COUNT_META.consult.color,
  ETC: SUMMARY_EXTRA_COUNT_META.etc.color,
};
const VISIBLE_ROWS = 4;
const VISIBLE_BORDER = '#E6ECEE';

const TABS = [
  { value: 'time', label: '시간표' },
  { value: 'counts', label: '표시 항목' },
  { value: 'durations', label: '기본 수업 시간' },
  { value: 'rooms', label: '강의실' },
  { value: 'staff', label: '직원 순서' },
];

const sameOrder = (a, b) => a.length === b.length && a.every((id, index) => id === b[index]);

// 직원 순서 목록 — 끌어 놓기 또는 위/아래 버튼으로 순서를 바꾼다. 맨 위가 일간 보기의 맨 왼쪽 컬럼이다.
const StaffOrderList = ({ items, onMove }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const endDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };
  return (
    <Box sx={{ border: '1px solid #E6ECEE', borderRadius: '8px', backgroundColor: '#fff', overflow: 'hidden' }}>
      {items.map((staff, index) => (
        <Box
          key={staff.trainerId}
          draggable
          onDragStart={e => {
            e.dataTransfer.effectAllowed = 'move';
            // Firefox 는 데이터가 없으면 드래그를 시작하지 않는다
            e.dataTransfer.setData('text/plain', String(staff.trainerId));
            setDragIndex(index);
          }}
          onDragOver={e => {
            if (dragIndex === null) return;
            e.preventDefault();
            setOverIndex(index);
          }}
          onDrop={e => {
            e.preventDefault();
            if (dragIndex !== null && dragIndex !== index) onMove(dragIndex, index);
            endDrag();
          }}
          onDragEnd={endDrag}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '44px',
            pl: '8px',
            pr: '6px',
            cursor: 'grab',
            userSelect: 'none',
            borderTop: index > 0 ? '1px solid #E6ECEE' : 'none',
            opacity: dragIndex === index ? 0.35 : 1,
            boxShadow:
              overIndex === index && dragIndex !== null && dragIndex !== index
                ? `inset 0 ${dragIndex < index ? -3 : 3}px 0 ${PRIMARY}`
                : 'none',
            '&:hover': { backgroundColor: '#F7FAFA' },
            '&:active': { cursor: 'grabbing' },
          }}>
          <DragIndicatorIcon sx={{ fontSize: '18px', color: '#B5BEC4' }} />
          <Typography sx={{ width: '22px', fontSize: '12px', color: '#8A939C', textAlign: 'right' }}>{index + 1}</Typography>
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: '#262626' }}>
            {staff.trainerName}
            {staff.employeePositionName && (
              <Box component="span" sx={{ ml: '6px', fontSize: '12px', fontWeight: 400, color: '#8A939C' }}>
                {staff.employeePositionName}
              </Box>
            )}
          </Typography>
          <IconButton size="small" aria-label={`${staff.trainerName} 위로`} disabled={index === 0} onClick={() => onMove(index, index - 1)}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`${staff.trainerName} 아래로`}
            disabled={index === items.length - 1}
            onClick={() => onMove(index, index + 1)}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

export const Help = ({ children }) => (
  <Typography sx={{ fontSize: '12px', color: '#8A939C', lineHeight: 1.5, mt: '4px' }}>{children}</Typography>
);

// 라벨(좌측 고정폭) | 입력 + 아래 한 줄 도움말
export const FieldRow = ({ label, help, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', '& + &': { mt: '18px' } }}>
    <Typography
      sx={{ width: LABEL_WIDTH, flexShrink: 0, pt: '8px', fontSize: '13px', fontWeight: 600, color: '#4D4D4D' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>{children}</Box>
      {help && <Help>{help}</Help>}
    </Box>
  </Box>
);

// 세그먼트 토글 (30분 | 1시간 | 2시간). compact 는 선택지가 많을 때 좌우 여백을 줄인다. isDisabled(option) 이면 못 고른다
export const Segment = ({ options, value, onChange, renderLabel, compact = false, isDisabled }) => (
  <Box sx={{ display: 'inline-flex', border: `1px solid ${PRIMARY}`, borderRadius: '8px', overflow: 'hidden' }}>
    {options.map((option, index) => {
      const active = option === value;
      const disabled = !!isDisabled?.(option);
      return (
        <Box
          key={option}
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => onChange(option)}
          sx={{
            height: '36px',
            px: compact ? '12px' : '18px',
            whiteSpace: 'nowrap',
            border: 'none',
            borderLeft: index > 0 ? `1px solid ${PRIMARY}` : 'none',
            backgroundColor: active ? PRIMARY : '#fff',
            color: active ? '#fff' : PRIMARY_DARK,
            fontSize: '13px',
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            ...(disabled && { opacity: 0.4 }),
          }}>
          {renderLabel(option)}
        </Box>
      );
    })}
  </Box>
);

// 표시 항목 한 줄 — 색 점 + 이름(+ 카드 포함) + 스위치. 줄 전체가 label 이라 어디를 눌러도 토글된다.
const VisibleItemRow = ({ label, color, withCard, checked, onChange, index }) => (
  <Box
    component="label"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      height: '46px',
      pl: '14px',
      pr: '6px',
      cursor: 'pointer',
      borderTop: index % VISIBLE_ROWS > 0 ? `1px solid ${VISIBLE_BORDER}` : 'none',
      borderLeft: index >= VISIBLE_ROWS ? `1px solid ${VISIBLE_BORDER}` : 'none',
      transition: 'background-color 120ms ease',
      '&:hover': { backgroundColor: '#F7FAFA' },
    }}>
    <Box
      component="span"
      sx={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        flexShrink: 0,
        backgroundColor: color,
        opacity: checked ? 1 : 0.35,
        transition: 'opacity 120ms ease',
      }}
    />
    <Typography
      component="span"
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: '13px',
        fontWeight: 600,
        color: checked ? '#262626' : '#8A939C',
        transition: 'color 120ms ease',
      }}>
      {label}
      {withCard && (
        <Box component="span" sx={{ ml: '6px', fontSize: '11px', fontWeight: 400, color: '#A3ABB2' }}>
          카드 포함
        </Box>
      )}
    </Typography>
    <Switch
      size="small"
      checked={checked}
      onChange={onChange}
      inputProps={{ 'aria-label': `${label} 표시` }}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: PRIMARY, opacity: 0.5 },
      }}
    />
  </Box>
);

// 탭 라벨 + 오류 빨간 점
export const TabLabel = ({ label, hasError }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
    {label}
    {hasError && (
      <Box
        component="span"
        aria-label="입력 오류"
        sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#E5484D' }}
      />
    )}
  </Box>
);

export const selectSx = { height: '36px', fontSize: '14px', backgroundColor: '#fff' };

export const tabsSx = {
  flexShrink: 0,
  minHeight: '44px',
  borderBottom: '1px solid #E6ECEE',
  backgroundColor: '#fff',
  '& .MuiTab-root': { minHeight: '44px', fontSize: '14px', fontWeight: 600, color: '#8A939C', textTransform: 'none' },
  '& .MuiTab-root.Mui-selected': { color: PRIMARY_DARK },
  '& .MuiTabs-indicator': { backgroundColor: PRIMARY, height: '3px' },
};

// 설정을 불러오는 중·실패 안내 띠
export const SettingStatusBar = ({ error, children }) => (
  <Box
    role="status"
    sx={{
      flexShrink: 0,
      px: '24px',
      py: '10px',
      fontSize: '13px',
      lineHeight: 1.5,
      color: error ? '#B42318' : '#4D4D4D',
      backgroundColor: error ? '#FEF3F2' : '#F1FAFB',
      borderBottom: '1px solid #E6ECEE',
    }}>
    {children}
  </Box>
);

const footerButtonSx = {
  height: '40px',
  borderRadius: '8px',
  fontWeight: 600,
};
const cancelButtonSx = {
  ...footerButtonSx,
  px: '20px',
  backgroundColor: '#E6E6E6',
  color: '#666',
  '&:hover': { backgroundColor: '#DADADA' },
};
const primaryButtonSx = {
  ...footerButtonSx,
  px: '28px',
  backgroundColor: PRIMARY,
  color: '#fff',
  '&:hover': { backgroundColor: '#3a9aa2' },
  '&.Mui-disabled': { color: '#fff', opacity: 0.6 },
};

/**
 * 스케줄 설정 모달 틀 — 배경을 거의 가리지 않아 바꾼 값이 모달 바깥 뒤 스케줄에 미리보기로 보인다.
 * 시간표 본문을 덜 가리도록 가운데보다 위쪽에 띄운다.
 * changed 면 [취소]·✕·바깥 클릭·Esc 로 닫을 때 확인을 받는다. 지점·그룹 스케줄 설정 모달이 함께 쓴다.
 */
export const SettingDialog = ({ open, title, changed, saving, onSave, onClose, tabs, status, children }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) setConfirmOpen(false);
  }, [open]);

  // 저장 중에는 Esc·✕·바깥 클릭·취소로 닫지 않는다(요청이 끝난 뒤 결과에 따라 닫히거나 남는다)
  const requestClose = () => {
    if (saving) return;
    if (changed) setConfirmOpen(true);
    else onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        maxWidth={false}
        BackdropProps={{ sx: { backgroundColor: 'rgba(15, 23, 42, 0.06)' } }}
        PaperProps={{
          sx: {
            m: 0,
            width: '600px',
            maxWidth: 'calc(100vw - 32px)',
            // 다른 모달처럼 화면 가운데. 탭마다 내용 높이가 달라 높이를 고정해 탭 전환 때 모달이 움직이지 않게 한다
            height: '600px',
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.22)',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: '48px',
            px: '24px',
            pr: '12px',
            flexShrink: 0,
            backgroundColor: HEADER_BG,
          }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff', flexShrink: 0 }}>{title}</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {changed && (
              <Typography
                role="status"
                noWrap
                sx={{ fontSize: '12px', color: '#9BD9DE', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Box
                  component="span"
                  sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: PRIMARY, flexShrink: 0 }}
                />
                미리보기 중 — 저장해야 적용됩니다
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            aria-label="닫기"
            disabled={saving}
            onClick={requestClose}
            sx={{ color: 'rgba(255,255,255,0.8)', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {tabs}
        {status}

        {/* 탭마다 높이가 달라도 모달이 들썩이지 않게 최소 높이를 둔다. 작은 화면에선 본문만 스크롤 */}
        <Box sx={{ flex: '1 1 auto', minHeight: '300px', overflowY: 'auto', p: '24px', backgroundColor: '#fafbfc' }}>
          {children}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            p: '12px 24px 16px',
            flexShrink: 0,
            borderTop: '1px solid #E6ECEE',
            backgroundColor: '#fff',
          }}>
          <Button disabled={saving} onClick={requestClose} sx={cancelButtonSx}>
            취소
          </Button>
          <Button disabled={saving} onClick={onSave} sx={primaryButtonSx}>
            저장
          </Button>
        </Box>
      </Dialog>

      {/* 저장하지 않은 변경 확인 — 닫으면 미리보기도 원래 값으로 돌아간다 */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { width: '360px', maxWidth: 'calc(100vw - 32px)', borderRadius: '8px', overflow: 'hidden' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', height: '48px', px: '24px', backgroundColor: HEADER_BG }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>변경사항 확인</Typography>
        </Box>
        <Typography sx={{ p: '24px', fontSize: '14px', lineHeight: 1.6, color: '#4D4D4D' }}>
          저장하지 않은 변경사항이 있습니다.
          <br />
          닫으면 변경한 내용은 적용되지 않습니다.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', p: '0 24px 16px' }}>
          <Button autoFocus onClick={() => setConfirmOpen(false)} sx={cancelButtonSx}>
            계속 편집
          </Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              onClose();
            }}
            sx={primaryButtonSx}>
            닫기
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

// 스케줄 설정 API 로 저장하는 입력 묶음 — 설정을 아직 못 받았으면(로딩·실패) 통째로 막는다.
// 기본값이 그대로 저장돼 서버 설정을 덮어쓰지 않게 하려는 것이다.
export const SettingFields = ({ disabled, sx, children }) => (
  <Box
    component="fieldset"
    disabled={disabled}
    sx={{
      m: 0,
      p: 0,
      border: 'none',
      minWidth: 0,
      ...(disabled && { opacity: 0.5, pointerEvents: 'none' }),
      ...sx,
    }}>
    {children}
  </Box>
);

const toDurationInputs = setting =>
  Object.fromEntries(DURATION_TYPES.map(({ key }) => [key, String(setting.defaultDurations[key])]));

// 저장 payload 모양. 표시 순서는 칩 순서로 고정한다.
const toSettingPayload = (setting, defaultDurations) => ({
  timeUnitMinutes: setting.timeUnitMinutes,
  snapMinutes: setting.snapMinutes,
  cardMinMinutes: setting.cardMinMinutes,
  defaultDurations,
  defaultRoomIds: setting.defaultRoomIds,
  visibleCounts: COUNT_CHIPS.map(({ key }) => key).filter(key => setting.visibleCounts.includes(key)),
  weekStartDay: setting.weekStartDay,
});

/**
 * 지점 스케줄 설정 모달 — 시간표(시작·종료, 표 칸·선택 단위, 카드 최소 높이) / 요약 카운트 / 기본 수업 시간 / 기본 강의실.
 * 캘린더 시작·종료는 기존 지점 시간 API, 나머지는 지점 스케줄 설정 API 로 저장한다.
 */
const BranchScheduleSettingModal = ({
  open,
  onClose,
  branchId,
  token,
  periodTimes,
  onPeriodTimesSaved,
  setting,
  settingLoading = false, // 설정 조회 중(아직 못 받음)
  settingError = false, // 설정 조회 실패
  rooms = [],
  canManageRooms = false,
  staffList = [], // 지점 직원(일간 보기 컬럼) — 지금 저장된 순서
  onPreviewChange, // 저장 전 미리보기 { setting, times } — 바뀐 게 없으면 null. 화면이 이 값으로 그린다
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [draftTimes, setDraftTimes] = useState({ startTime: 0, endTime: 23 });
  const [draft, setDraftState] = useState(setting);
  // 기본 수업 시간 입력 중 값(문자열) — 지우고 새로 칠 수 있게 숫자로 바로 바꾸지 않는다
  const [durationInputs, setDurationInputsState] = useState({});
  // 이번에 연 뒤 설정 입력을 건드렸는지 — 안 건드렸으면 조회가 늦게 끝나도 그 값으로 다시 채운다
  const [settingDirty, setSettingDirty] = useState(false);
  const settingReady = !settingLoading && !settingError;
  const [saving, setSaving] = useState(false);
  const [roomManageOpen, setRoomManageOpen] = useState(false);
  const [tab, setTab] = useState('time');
  // 이번에 연 뒤 입력을 저장값으로 맞췄는지 — 맞추기 전(직전에 닫을 때 남은 입력)으로 미리보기를 내보내지 않는다
  const [synced, setSynced] = useState(false);
  // 직원 순서(trainerId 배열) — 연 뒤 안 건드렸으면 저장값이 바뀔 때 따라간다
  const savedStaffIds = staffList.map(staff => staff.trainerId);
  const [staffOrder, setStaffOrder] = useState([]);
  const [staffDirty, setStaffDirty] = useState(false);

  // 현재 저장된 캘린더 시각 — 모달 초기값이자, 바뀌었는지(시간 API 를 보낼지) 판단 기준
  const savedTimes = {
    startTime: periodTimes?.startTime != null ? Number(periodTimes.startTime) : 0,
    endTime: periodTimes?.endTime != null ? Number(periodTimes.endTime) : 23,
  };

  // 사용자 입력용 setter — 설정을 건드렸다고 표시한다. 설정을 아직 못 받았으면 입력을 받지 않는다(키보드 입력 포함)
  const setDraft = updater => {
    if (!settingReady) return;
    setSettingDirty(true);
    setDraftState(updater);
  };
  const setDurationInputs = updater => {
    if (!settingReady) return;
    setSettingDirty(true);
    setDurationInputsState(updater);
  };

  // 열 때마다 현재 저장값으로 초기화 — 취소 후 다시 열면 이전 입력이 남지 않는다
  useEffect(() => {
    if (!open) {
      setSynced(false);
      return;
    }
    setSynced(true);
    setDraftTimes(savedTimes);
    setTab('time');
    setSettingDirty(false);
    setDraftState(setting);
    setDurationInputsState(toDurationInputs(setting));
    setStaffDirty(false);
    setStaffOrder(savedStaffIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 직원 목록·저장 순서가 바뀌면(조회 완료·다른 곳에서 변경) 아직 안 건드린 순서를 맞춘다
  const savedStaffKey = savedStaffIds.join(',');
  useEffect(() => {
    if (!open || staffDirty) return;
    setStaffOrder(savedStaffIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedStaffKey]);

  // 편집 중 순서 — 그사이 빠진 직원은 지우고 새로 생긴 직원은 뒤에 붙인다
  const draftStaffIds = [
    ...staffOrder.filter(id => savedStaffIds.includes(id)),
    ...savedStaffIds.filter(id => !staffOrder.includes(id)),
  ];
  const staffById = new Map(staffList.map(staff => [staff.trainerId, staff]));
  const moveStaff = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= draftStaffIds.length) return;
    const next = [...draftStaffIds];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setStaffDirty(true);
    setStaffOrder(next);
  };

  // 연 뒤에 조회가 끝나면(또는 다시 받으면) 아직 안 건드린 입력을 그 값으로 맞춘다
  useEffect(() => {
    if (!open || settingDirty) return;
    setDraftState(setting);
    setDurationInputsState(toDurationInputs(setting));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setting]);

  const toggleCount = key =>
    setDraft(prev => ({
      ...prev,
      visibleCounts: prev.visibleCounts.includes(key)
        ? prev.visibleCounts.filter(item => item !== key)
        : [...prev.visibleCounts, key],
    }));

  const invalidDurationKeys = DURATION_TYPES.filter(
    ({ key }) => !isValidDuration(Number(durationInputs[key])),
  ).map(({ key }) => key);

  // 종료는 시작보다 늦어야 한다(같은 시각도 불가)
  const timeRangeInvalid = draftTimes.endTime <= draftTimes.startTime;
  const tabErrors = { time: timeRangeInvalid, durations: invalidDurationKeys.length > 0 };

  const payload = toSettingPayload(
    draft,
    Object.fromEntries(DURATION_TYPES.map(({ key }) => [key, Number(durationInputs[key])])),
  );
  // 캘린더 시각을 바꾸지 않았으면 시간 API 는 보내지 않는다
  const timesChanged = draftTimes.startTime !== savedTimes.startTime || draftTimes.endTime !== savedTimes.endTime;
  // 설정은 받아 온 값이 있고 그 값과 달라졌을 때만 보낸다 — 못 받았으면 기본값으로 덮어쓰게 되므로 보내지 않는다
  const settingChanged =
    settingReady && JSON.stringify(payload) !== JSON.stringify(toSettingPayload(setting, setting.defaultDurations));
  const orderChanged = !sameOrder(draftStaffIds, savedStaffIds);
  const changed = timesChanged || settingChanged || orderChanged;

  // 미리보기 — 바뀐 값을 저장 전에 화면에 보여 준다(서버 캐시는 건드리지 않는다). 시각 범위가 틀리면 시각은 저장값으로 둔다
  useEffect(() => {
    if (!open || !synced) return;
    onPreviewChange?.(
      changed
        ? {
            setting: draft,
            times: timeRangeInvalid ? null : draftTimes,
            staffOrder: orderChanged ? draftStaffIds : null,
          }
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, synced, changed, draft, draftTimes, timeRangeInvalid, draftStaffIds.join(',')]);

  // 기본 수업 시간 스테퍼 — 5분 단위, 범위 안으로
  const stepDuration = (key, direction) =>
    setDurationInputs(prev => {
      const current = Number(prev[key]);
      const base = Number.isFinite(current) ? Math.round(current / DURATION_STEP) * DURATION_STEP : DURATION_MIN;
      const next = Math.min(Math.max(base + direction * DURATION_STEP, DURATION_MIN), DURATION_MAX);
      return { ...prev, [key]: String(next) };
    });

  const handleSave = async () => {
    // 오류가 있으면 그 탭으로 옮겨 보여준다
    if (timeRangeInvalid) {
      setTab('time');
      toast.error('종료 시각은 시작 시각 이후여야 합니다');
      return;
    }
    if (settingReady && invalidDurationKeys.length > 0) {
      setTab('durations');
      toast.error(`기본 수업 시간은 ${DURATION_MIN}~${DURATION_MAX}분, ${DURATION_STEP}분 단위로 입력해 주세요`);
      return;
    }

    // 기본 강의실이 지금 목록에 없으면(강의실 관리에서 삭제 등) 비워서 보낸다 — 화면도 '선택 안 함'으로 보인다.
    // 강의실 목록을 아직 못 받았으면(로딩·실패로 빈 목록) 저장값을 지우지 않고 그대로 보낸다.
    const roomIds = new Set(rooms.map(getRoomId));
    const payloadToSend = {
      ...payload,
      defaultRoomIds: Object.fromEntries(
        Object.entries(payload.defaultRoomIds).map(([key, roomId]) => [
          key,
          rooms.length === 0 || roomIds.has(roomId) ? roomId : null,
        ]),
      ),
    };

    if (!timesChanged && !settingChanged && !orderChanged) {
      onClose();
      return;
    }

    setSaving(true);
    const staffIdsToSave = draftStaffIds;
    const [timeResult, settingResult, orderResult] = await Promise.allSettled([
      timesChanged ? updateBranchTime(draftTimes, branchId, token) : Promise.resolve(null),
      settingChanged ? updateBranchScheduleSetting(branchId, payloadToSend, token) : Promise.resolve(null),
      // 일간 머리글 드래그·화살표와 같은 API — 배열 순서가 곧 sortOrder
      orderChanged ? updateBranchTrainerOrder(branchId, staffIdsToSave, token) : Promise.resolve(null),
    ]);
    // 새 시각을 받아 온 뒤에 닫는다 — 미리보기를 걷을 때 이전 시각이 잠깐 보이지 않게
    if (timesChanged && timeResult.status === 'fulfilled') await onPeriodTimesSaved?.();
    setSaving(false);

    if (settingChanged && settingResult.status === 'fulfilled') {
      // PUT 응답은 저장된 값 전체다 — 그대로 캐시에 넣어 화면에 바로 반영한다
      const saved =
        settingResult.value && typeof settingResult.value === 'object' ? settingResult.value : payloadToSend;
      queryClient.setQueryData(branchScheduleSettingQueryKey(branchId), saved);
    }
    if (orderChanged && orderResult.status === 'fulfilled') {
      // 일간 머리글에서 순서를 바꿀 때와 같이 두 목록 캐시의 sortOrder 를 맞춘다(직원 목록은 sortOrder 로 정렬된다)
      const nextSortOrders = new Map(staffIdsToSave.map((id, index) => [id, index]));
      queryClient.setQueryData(['trainers', branchId], prev =>
        prev?.map(trainer => ({ ...trainer, sortOrder: nextSortOrders.get(trainer.trainerId) ?? trainer.sortOrder })),
      );
      queryClient.setQueryData(['branchManagers', branchId], prev =>
        prev?.map(manager => ({ ...manager, sortOrder: nextSortOrders.get(manager.employeeId) ?? manager.sortOrder })),
      );
      // 헤더 드래그 경로(updateTrainerOrderMutation.onSuccess)와 같이 서버 값으로 다시 받는다
      queryClient.invalidateQueries({ queryKey: ['trainers', branchId] });
      queryClient.invalidateQueries({ queryKey: ['branchManagers', branchId] });
    }

    if (
      timeResult.status === 'fulfilled' &&
      settingResult.status === 'fulfilled' &&
      orderResult.status === 'fulfilled'
    ) {
      toast.success('설정을 저장했습니다');
      onClose();
      return;
    }
    // 실패하면 모달을 열어 둔 채 실패한 쪽만 알린다(성공한 쪽은 이미 반영됐다). 둘 다 실패하면 토스트 하나에 줄바꿈으로
    const failures = [
      timeResult.status === 'rejected' && `캘린더 시간 저장 실패: ${errorMessages(timeResult.reason)}`,
      settingResult.status === 'rejected' && `스케줄 설정 저장 실패: ${errorMessages(settingResult.reason)}`,
      orderResult.status === 'rejected' && `직원 순서 저장 실패: ${errorMessages(orderResult.reason)}`,
    ].filter(Boolean);
    toast.error(toMultilineToast(failures.join('\n')));
  };

  return (
    <>
      <SettingDialog
        open={open}
        title="지점 스케줄 설정"
        changed={changed}
        saving={saving}
        onSave={handleSave}
        onClose={onClose}
        tabs={
          // 탭 — 전환해도 입력값은 모달 state 에 그대로 남는다
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth" sx={tabsSx}>
            {TABS.map(item => (
              <Tab key={item.value} value={item.value} label={<TabLabel label={item.label} hasError={!!tabErrors[item.value]} />} />
            ))}
          </Tabs>
        }
        status={
          !settingReady && (
            <SettingStatusBar error={settingError}>
              {settingError
                ? '스케줄 설정을 불러오지 못했습니다. 지금은 캘린더 시간만 저장됩니다. 새로고침 후 다시 시도해 주세요.'
                : '스케줄 설정을 불러오는 중입니다. 캘린더 시간 외 항목은 불러온 뒤 수정할 수 있습니다.'}
            </SettingStatusBar>
          )
        }>
        {tab === 'time' && (
          <>
            <FieldRow
              label="캘린더 시간"
              help={
                timeRangeInvalid ? (
                  <Box component="span" sx={{ color: '#E5484D' }}>
                    종료 시각은 시작 시각보다 늦어야 합니다.
                  </Box>
                ) : (
                  '일간·주간 보기에 표시할 시간 범위입니다.'
                )
              }>
              <Select
                size="small"
                sx={{ ...selectSx, width: '110px' }}
                value={draftTimes.startTime}
                onChange={e => setDraftTimes(prev => ({ ...prev, startTime: e.target.value }))}>
                {HOURS.map(hour => (
                  <MenuItem key={hour.value} value={hour.value}>
                    {hour.title}
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ color: '#8A939C' }}>~</Typography>
              <Select
                size="small"
                error={timeRangeInvalid}
                sx={{ ...selectSx, width: '110px' }}
                value={draftTimes.endTime}
                onChange={e => setDraftTimes(prev => ({ ...prev, endTime: e.target.value }))}>
                {HOURS.map(hour => (
                  <MenuItem key={hour.value} value={hour.value} disabled={hour.value <= draftTimes.startTime}>
                    {hour.title}
                  </MenuItem>
                ))}
              </Select>
            </FieldRow>
            <SettingFields disabled={!settingReady} sx={{ mt: '18px' }}>
              <FieldRow label="표 칸 단위" help="시간표 한 칸(왼쪽 시간 줄)의 길이입니다.">
                <Segment
                  options={TIME_UNIT_OPTIONS}
                  value={draft.timeUnitMinutes}
                  onChange={value => setDraft(withTimeUnit(value))}
                  renderLabel={value => TIME_UNIT_LABEL[value]}
                />
              </FieldRow>
              <FieldRow label="칸 선택 크기" help="스케줄 표의 빈칸에 마우스를 올리거나 클릭·드래그할 때 선택되는 시간 간격입니다.">
                <Segment
                  compact
                  options={SNAP_OPTIONS}
                  value={draft.snapMinutes}
                  onChange={value => setDraft(prev => ({ ...prev, snapMinutes: value }))}
                  isDisabled={value => value > draft.timeUnitMinutes}
                  renderLabel={toSnapLabel}
                />
              </FieldRow>
              <FieldRow label="카드 최소 높이" help="1시간 칸 높이 대비 짧은 수업 카드의 최소 높이입니다.">
                <Select
                  size="small"
                  sx={{ ...selectSx, width: '150px' }}
                  value={draft.cardMinMinutes}
                  onChange={e => setDraft(prev => ({ ...prev, cardMinMinutes: e.target.value }))}>
                  {CARD_MIN_OPTIONS.map(value => (
                    <MenuItem key={value} value={value}>
                      {value === DEFAULT_CARD_MIN_MINUTES ? `${toCardMinPercent(value)}% (기본)` : `${toCardMinPercent(value)}%`}
                    </MenuItem>
                  ))}
                </Select>
              </FieldRow>
              <FieldRow label="주 시작 요일" help="주간·월간 보기의 첫 요일입니다.">
                <Segment
                  compact
                  options={WEEK_START_DAY_OPTIONS}
                  value={draft.weekStartDay}
                  onChange={value => setDraft(prev => ({ ...prev, weekStartDay: value }))}
                  renderLabel={value => WEEK_START_DAY_LABEL[value]}
                />
              </FieldRow>
            </SettingFields>
          </>
        )}

        {tab === 'counts' && (
          <SettingFields disabled={!settingReady}>
            <Help>끄면 지점 스케줄에서 숨깁니다. 카드 포함 항목은 일정 카드까지 함께 숨겨집니다.</Help>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridTemplateRows: `repeat(${VISIBLE_ROWS}, auto)`,
                gridAutoFlow: 'column',
                mt: '12px',
                border: `1px solid ${VISIBLE_BORDER}`,
                borderRadius: '8px',
                backgroundColor: '#fff',
                overflow: 'hidden',
              }}>
              {COUNT_CHIPS.map(({ key, label }, index) => (
                <VisibleItemRow
                  key={key}
                  index={index}
                  label={label}
                  color={VISIBLE_ITEM_COLOR[key]}
                  withCard={key in TYPE_COUNT_KEY_TO_CLASS_TYPE}
                  checked={draft.visibleCounts.includes(key)}
                  onChange={() => toggleCount(key)}
                />
              ))}
            </Box>
          </SettingFields>
        )}

        {tab === 'durations' && (
          <SettingFields disabled={!settingReady}>
            <Typography sx={{ fontSize: '13px', color: '#4D4D4D', mb: '14px' }}>
              새 일정을 만들 때 종료 시각이 시작 + 이 시간으로 채워집니다. ({DURATION_MIN}~{DURATION_MAX}분, {DURATION_STEP}분 단위)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: '24px', rowGap: '14px' }}>
              {DURATION_TYPES.map(({ key, label }) => {
                const hasError = invalidDurationKeys.includes(key);
                return (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Typography sx={{ width: '64px', flexShrink: 0, fontSize: '13px', fontWeight: 600, color: '#4D4D4D' }}>
                      {label}
                    </Typography>
                    <IconButton size="small" aria-label={`${label} 5분 줄이기`} onClick={() => stepDuration(key, -1)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      size="small"
                      type="number"
                      value={durationInputs[key] ?? ''}
                      error={hasError}
                      onChange={e => setDurationInputs(prev => ({ ...prev, [key]: e.target.value }))}
                      inputProps={{ min: DURATION_MIN, max: DURATION_MAX, step: DURATION_STEP, 'aria-label': `${label} 기본 수업 시간` }}
                      InputProps={{ endAdornment: <InputAdornment position="end">분</InputAdornment> }}
                      sx={{
                        width: '92px',
                        '& .MuiInputBase-root': { height: '36px', pr: '10px', backgroundColor: '#fff' },
                        '& .MuiInputBase-input': { pl: '10px', pr: '2px', textAlign: 'right' },
                        '& .MuiInputAdornment-root': { ml: '4px' },
                        // 숫자 입력 기본 화살표는 옆 −/+ 버튼과 겹치므로 숨긴다
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': { MozAppearance: 'textfield' },
                      }}
                    />
                    <IconButton size="small" aria-label={`${label} 5분 늘리기`} onClick={() => stepDuration(key, 1)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </SettingFields>
        )}

        {tab === 'rooms' && (
          <SettingFields disabled={!settingReady}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '18px' }}>
              <Typography sx={{ flex: 1, fontSize: '13px', color: '#4D4D4D' }}>
                새 일정을 만들 때 레슨 구분에 따라 먼저 선택되는 강의실입니다.
              </Typography>
              {canManageRooms && (
                <Button
                  size="small"
                  onClick={() => setRoomManageOpen(true)}
                  sx={{
                    flexShrink: 0,
                    height: '32px',
                    px: '12px',
                    color: PRIMARY_DARK,
                    border: `1px solid ${PRIMARY}`,
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: '#F1FAFB' },
                  }}>
                  강의실 관리
                </Button>
              )}
            </Box>
            {ROOM_DEFAULT_TYPES.map(({ key, label }) => {
              const selected = draft.defaultRoomIds[key];
              const options = filterRoomsByLessonType(rooms, key, selected);
              return (
                <FieldRow key={key} label={label}>
                  <Select
                    size="small"
                    displayEmpty
                    sx={{ ...selectSx, width: '100%', maxWidth: '320px' }}
                    value={options.some(room => room.branchExerciseRoomId === selected) ? selected : ''}
                    onChange={e =>
                      setDraft(prev => ({
                        ...prev,
                        defaultRoomIds: { ...prev.defaultRoomIds, [key]: e.target.value === '' ? null : e.target.value },
                      }))
                    }>
                    <MenuItem value="">선택 안 함</MenuItem>
                    {options.map(room => (
                      <MenuItem key={room.branchExerciseRoomId} value={room.branchExerciseRoomId}>
                        {room.branchExerciseRoomName}
                      </MenuItem>
                    ))}
                  </Select>
                </FieldRow>
              );
            })}
          </SettingFields>
        )}
        {tab === 'staff' && (
          <>
            <Typography sx={{ fontSize: '13px', color: '#4D4D4D', mb: '14px' }}>
              일간 보기의 직원 컬럼 순서입니다. 끌어 놓거나 위/아래 버튼으로 바꿉니다. 맨 위가 맨 왼쪽입니다.
            </Typography>
            {draftStaffIds.length === 0 ? (
              <Typography sx={{ fontSize: '13px', color: '#8A939C' }}>등록된 직원이 없습니다.</Typography>
            ) : (
              <StaffOrderList items={draftStaffIds.map(id => staffById.get(id))} onMove={moveStaff} />
            )}
          </>
        )}
      </SettingDialog>

      {canManageRooms && (
        <ClassroomManageModal
          open={roomManageOpen}
          onClose={() => setRoomManageOpen(false)}
          branchId={branchId}
          rooms={rooms}
        />
      )}
    </>
  );
};

export default BranchScheduleSettingModal;
